import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Download, Loader2, ThumbsUp, ThumbsDown, Clock, Target, PartyPopper, User, Users, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { api } from '../App';
import { toast } from 'sonner';
import { trackActivityStarted, trackActivityCompleted, trackAIInsightGenerated, trackAIInsightFeedback, trackHomeworkChecked } from '../utils/analytics';

// Helper function to parse tasks with their "Why" explanations
const parseTasksWithWhy = (content) => {
  const tasks = [];
  let currentTask = null;
  
  content.forEach((line) => {
    const trimmedLine = line.trim();
    // Check if it's a new task (starts with bullet)
    if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
      if (currentTask) {
        tasks.push(currentTask);
      }
      let taskText = trimmedLine.replace(/^[•\-]\s*/, '');
      let taskType = null;
      
      // Extract task type (SOLO or OUTREACH)
      if (taskText.toUpperCase().startsWith('SOLO:')) {
        taskType = 'solo';
        taskText = taskText.replace(/^SOLO:\s*/i, '');
      } else if (taskText.toUpperCase().startsWith('OUTREACH:')) {
        taskType = 'outreach';
        taskText = taskText.replace(/^OUTREACH:\s*/i, '');
      }
      
      currentTask = {
        text: taskText,
        type: taskType,
        why: null
      };
    }
    // Check if it's a "Why:" line
    else if (trimmedLine.toLowerCase().startsWith('why:') && currentTask) {
      currentTask.why = trimmedLine.replace(/^why:\s*/i, '');
    }
    // Append to current task if it's a continuation
    else if (currentTask && trimmedLine.length > 0) {
      // If we haven't found a why yet, append to task text
      if (!currentTask.why) {
        currentTask.text += ' ' + trimmedLine;
      }
    }
  });
  
  // Don't forget the last task
  if (currentTask) {
    tasks.push(currentTask);
  }
  
  return tasks;
};

// Helper function to format the insight text with proper styling and checkboxes
const formatInsight = (text, taskCompletions, onTaskToggle) => {
  const lines = text.split('\n');
  const sections = [];
  let currentSection = null;
  let crisisWarning = null;

  // Check if there's a crisis warning at the start
  if (text.includes('⚠️ IMPORTANT: Mental Health Support')) {
    const warningEndIndex = text.indexOf('---');
    if (warningEndIndex > -1) {
      crisisWarning = text.substring(0, warningEndIndex);
      // Remove the warning from the main text for section parsing
      text = text.substring(warningEndIndex + 3);
      lines.length = 0;
      lines.push(...text.split('\n'));
    }
  }

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Check if this is a section header (starts with "Section")
    if (trimmedLine.startsWith('Section ')) {
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        header: trimmedLine,
        content: []
      };
    } else if (trimmedLine.length > 0 && currentSection) {
      currentSection.content.push(trimmedLine);
    }
  });

  // Add the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  // Separate sections into categories
  let section4 = sections.find(s => s.header.includes('Section 4'));
  let section5 = sections.find(s => s.header.includes('Section 5'));
  const otherSections = sections.filter(s => !s.header.includes('Section 4') && !s.header.includes('Section 5'));
  
  // Determine which section has tasks (bullets) and which has the reflection question
  const section4HasBullets = section4?.content.some(line => 
    line.trim().startsWith('•') || line.trim().startsWith('-')
  );
  const section5HasBullets = section5?.content.some(line => 
    line.trim().startsWith('•') || line.trim().startsWith('-')
  );
  
  // If Section 4 has bullets (OLD format), swap them so reflection is always Section 4
  if (section4HasBullets && section5 && !section5HasBullets) {
    // Swap: reflection (currently Section 5) becomes Section 4, tasks (currently Section 4) becomes Section 5
    const tempContent = section4.content;
    const tempHeader = section4.header;
    
    // Extract the title part after the dash (e.g., "Something to try this week")
    const section4Title = section4.header.split('—')[1]?.trim() || 'Something to try this week';
    const section5Title = section5.header.split('—')[1]?.trim() || 'One question to sit with';
    
    section4 = {
      header: `Section 4 — ${section5Title}`,
      content: section5.content
    };
    section5 = {
      header: `Section 5 — ${section4Title}`,
      content: tempContent
    };
  }
  
  // Rebuild sections array with swapped Section 4/5
  const reorderedSections = [
    ...otherSections,
    section4,
    section5
  ].filter(Boolean).sort((a, b) => {
    // Sort by section number
    const aNum = parseInt(a.header.match(/Section (\d+)/)?.[1] || '0');
    const bNum = parseInt(b.header.match(/Section (\d+)/)?.[1] || '0');
    return aNum - bNum;
  });

  const renderedSections = reorderedSections.map((section, idx) => {
    const isSection4 = section.header.includes('Section 4');
    const isSection5 = section.header.includes('Section 5');
    
    // Check if section contains bullet points (tasks)
    const hasBulletPoints = section.content.some(line => 
      line.trim().startsWith('•') || line.trim().startsWith('-')
    );
    
    // Section 4 - Always reflection question (after swap logic above)
    if (isSection4) {
      // Render as plain italic text (same size as other sections)
      return (
        <div key={idx} className="space-y-3">
          <h4 className="font-bold text-lg text-slate-900">
            {section.header}
          </h4>
          <p className="text-slate-700 italic leading-relaxed">
            {section.content.join(' ')}
          </p>
        </div>
      );
    }
    
    // Section 5 - Always tasks (after swap logic above)
    if (isSection5) {
      const tasks = parseTasksWithWhy(section.content);
      
      return (
        <div key={idx} className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-lg text-slate-900">
              Section 5 — Your Weekly Tasks
            </h4>
            {/* Progress indicator */}
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-300 rounded-full"
                  style={{ width: `${(Object.values(taskCompletions).filter(Boolean).length / tasks.length) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-slate-600">
                {Object.values(taskCompletions).filter(Boolean).length}/{tasks.length} done
              </span>
            </div>
          </div>
          <div className="space-y-3">
            {tasks.map((task, taskIdx) => {
              const taskKey = `task_${taskIdx + 1}`;
              const isCompleted = taskCompletions[taskKey] || false;
              
              return (
                <div 
                  key={taskIdx} 
                  className={`bg-white border-2 rounded-xl p-4 transition-all duration-200 ${
                    isCompleted 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                  data-testid={`task-card-${taskIdx + 1}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      onChange={() => onTaskToggle(taskKey)}
                      className="mt-0.5 h-6 w-6 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                      data-testid={`task-checkbox-${taskIdx + 1}`}
                    />
                    <div className="flex-1 min-w-0">
                      {/* Task type badge and time estimate */}
                      <div className="flex items-center gap-2 mb-2">
                        {task.type && (
                          <span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${
                            task.type === 'solo' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {task.type === 'solo' ? 'Solo' : 'Outreach'}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          ~20 mins
                        </span>
                      </div>
                      <p className={`font-medium leading-relaxed ${
                        isCompleted ? 'line-through text-slate-400' : 'text-slate-800'
                      }`}>
                        {task.text}
                      </p>
                      {task.why && (
                        <p className={`mt-2 text-sm ${
                          isCompleted ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                          <span className="font-semibold text-blue-600">Why:</span> {task.why}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    // Default rendering for other sections
    return (
      <div key={idx} className="space-y-3">
        <h4 className="font-bold text-lg text-slate-900">
          {section.header}
        </h4>
        <div className="text-slate-700 leading-relaxed space-y-2">
          {section.content.map((line, lineIdx) => {
            // Check if it's a numbered list item
            if (/^\d+\)/.test(line)) {
              return (
                <p key={lineIdx} className="ml-4">
                  {line}
                </p>
              );
            }
            // Regular paragraph or bullet point
            else if (line.startsWith('•') || line.startsWith('-')) {
              return (
                <p key={lineIdx} className="ml-4">
                  {line}
                </p>
              );
            }
            else {
              return (
                <p key={lineIdx}>
                  {line}
                </p>
              );
            }
          })}
        </div>
      </div>
    );
  });

  // If there's a crisis warning, prepend it as a prominent banner
  if (crisisWarning) {
    return (
      <>
        <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6 rounded-r-lg" data-testid="crisis-warning">
          <div className="flex items-start gap-3">
            <div className="text-red-600 text-2xl">⚠️</div>
            <div className="flex-1">
              <h4 className="font-bold text-lg text-red-900 mb-2">
                IMPORTANT: Mental Health Support
              </h4>
              <div className="text-red-800 space-y-2">
                <p>If you&apos;re experiencing mental health difficulties, please reach out for professional support:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Contact <strong>NHS 111</strong> for urgent advice</li>
                  <li>Speak to your <strong>GP or doctor</strong></li>
                  <li>Call <strong>Samaritans on 116 123</strong> (available 24/7)</li>
                </ul>
                <p className="mt-3 text-sm">Your wellbeing is important. This program is designed to support career development, but is not a substitute for professional mental health care.</p>
              </div>
            </div>
          </div>
        </div>
        {renderedSections}
      </>
    );
  }

  return renderedSections;
};

const WeekPage = ({ user }) => {
  const { weekNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get starting activity from URL query param (1-based, convert to 0-based index)
  const searchParams = new URLSearchParams(location.search);
  const activityParam = parseInt(searchParams.get('activity')) || 1;
  const initialActivity = Math.max(0, activityParam - 1); // Convert to 0-based index
  
  const [week, setWeek] = useState(null);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(initialActivity);
  const [responseText, setResponseText] = useState('');
  const [insight, setInsight] = useState(null);
  const [insightId, setInsightId] = useState(null);
  const [taskCompletions, setTaskCompletions] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const activityStartTime = useRef(null);
  const [loadingMessage, setLoadingMessage] = useState('Generating feedback...');
  const [isFirstActivity, setIsFirstActivity] = useState(false);
  const [showFirstActivityCard, setShowFirstActivityCard] = useState(false);

  // Check localStorage for first activity card state on mount
  useEffect(() => {
    const dismissed = localStorage.getItem('kodo_first_activity_card_dismissed');
    const cardWeek = localStorage.getItem('kodo_first_activity_card_week');
    const cardOrder = localStorage.getItem('kodo_first_activity_card_order');
    
    // Only show if not dismissed AND on the same activity where they first completed
    if (!dismissed && cardWeek === String(weekNumber) && cardOrder === String(currentActivityIndex + 1)) {
      setShowFirstActivityCard(true);
    } else {
      setShowFirstActivityCard(false);
    }
  }, [weekNumber, currentActivityIndex]);

  // Handle dismiss of first activity card - persist to localStorage
  const handleDismissFirstActivityCard = () => {
    setShowFirstActivityCard(false);
    localStorage.setItem('kodo_first_activity_card_dismissed', 'true');
    localStorage.removeItem('kodo_first_activity_card_week');
    localStorage.removeItem('kodo_first_activity_card_order');
  };

  // Handle finish week - check if all weeks are complete
  const handleFinishWeek = async () => {
    try {
      // Fetch progress to check if all 6 weeks are now complete
      const progressRes = await api.get(`/users/${user.id}/progress`);
      const weeksCompleted = progressRes.data.weeks_completed || [];
      
      if (weeksCompleted.length === 6) {
        // All weeks complete - go to completion page
        navigate('/completion');
      } else {
        // Not all weeks complete - go to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      // On error, default to dashboard
      console.error('Error checking progress:', error);
      navigate('/dashboard');
    }
  };

  // Rotating loading messages
  useEffect(() => {
    if (!submitting) return;
    
    const messages = [
      'Generating feedback...',
      'Analysing your response...',
      'Finding patterns...',
      'Crafting insights...',
      'Personalising recommendations...',
      'Almost there...'
    ];
    
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setLoadingMessage(messages[index]);
    }, 2500);
    
    return () => clearInterval(interval);
  }, [submitting]);

  // Reset state when activity index changes
  useEffect(() => {
    setResponseText('');
    setInsight(null);
    setInsightId(null);
    setTaskCompletions({});
    setFeedbackGiven(null);
    activityStartTime.current = null;
  }, [currentActivityIndex, weekNumber]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        const weekRes = await api.get(`/weeks/${weekNumber}`);
        
        // Filter activities based on user's weekly commitment
        const activitiesPerWeek = user.weekly_commitment === 60 ? 1 : user.weekly_commitment === 120 ? 2 : 3;
        const filteredActivities = weekRes.data.activities.slice(0, activitiesPerWeek);
        
        setWeek({
          ...weekRes.data,
          activities: filteredActivities
        });

        // Fetch all responses and insights for this user
        const responsesRes = await api.get(`/activities/responses/user/${user.id}`);
        const insightsRes = await api.get(`/insights/user/${user.id}`);
        
        // Check if this is user's first activity (no insights yet)
        const totalInsights = insightsRes.data.length;
        setIsFirstActivity(totalInsights === 0);
        
        // Get responses for this week
        const weekResponses = responsesRes.data.filter(r => r.week_number === parseInt(weekNumber));
        const weekInsights = insightsRes.data.filter(i => i.week_number === parseInt(weekNumber));

        // Check if there's existing data for current activity
        if (filteredActivities[currentActivityIndex]) {

          const currentActivityOrder = filteredActivities[currentActivityIndex].order;

          // Try to find by activity_order first (new stable method)
          let existingResponse = weekResponses.find(r => r.activity_order === currentActivityOrder);
          let existingInsight = weekInsights.find(i => i.activity_order === currentActivityOrder);

          // Fallback for legacy data without activity_order: deduplicate and use index
          if (!existingResponse && weekResponses.length > 0) {
            const sortedResponses = [...weekResponses].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            const uniqueResponses = [];
            const seenActivityIds = new Set();
            for (let i = sortedResponses.length - 1; i >= 0; i--) {
              const r = sortedResponses[i];
              if (!seenActivityIds.has(r.activity_id)) {
                seenActivityIds.add(r.activity_id);
                uniqueResponses.unshift(r);
              }
            }
            existingResponse = uniqueResponses[currentActivityIndex];
          }

          if (!existingInsight && weekInsights.length > 0) {
            const sortedInsights = [...weekInsights].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            const uniqueInsights = [];
            const seenInsightActivityIds = new Set();
            for (let i = sortedInsights.length - 1; i >= 0; i--) {
              const insight = sortedInsights[i];
              if (!seenInsightActivityIds.has(insight.activity_id)) {
                seenInsightActivityIds.add(insight.activity_id);
                uniqueInsights.unshift(insight);
              }
            }
            existingInsight = uniqueInsights[currentActivityIndex];
          }

          if (existingResponse) {
            setResponseText(existingResponse.response_text);
          }
          if (existingInsight) {
            setInsight(existingInsight.insight_text);
            setInsightId(existingInsight.id);
            setTaskCompletions(existingInsight.task_completions || {});
          }
        }
      } catch (error) {
        console.error('Error fetching week data:', error);
        toast.error('Failed to load week data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, weekNumber, currentActivityIndex, navigate]);

  // Track when user starts an activity (when they start typing)
  const handleResponseChange = (e) => {
    if (!activityStartTime.current && e.target.value.length > 0) {
      activityStartTime.current = Date.now();
      const currentActivity = week?.activities[currentActivityIndex];
      if (currentActivity) {
        trackActivityStarted(parseInt(weekNumber), currentActivity.title);
      }
    }
    setResponseText(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!responseText || responseText.trim().length < 20) {
      toast.error('Please write at least a few sentences before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const currentActivity = week.activities[currentActivityIndex];
      
      // Calculate time spent
      const timeSpentSeconds = activityStartTime.current 
        ? Math.round((Date.now() - activityStartTime.current) / 1000)
        : 0;
      
      const response = await api.post('/activities/responses', {
        user_id: user.id,
        activity_id: currentActivity.id,
        week_number: parseInt(weekNumber),
        activity_order: currentActivity.order,  // Stable identifier for matching
        response_text: responseText
      });

      // Track activity completion with time
      trackActivityCompleted(parseInt(weekNumber), currentActivity.title, timeSpentSeconds);
      trackAIInsightGenerated(parseInt(weekNumber));
      
      // Store time tracking in backend (fire and forget - don't wait)
      if (timeSpentSeconds > 0) {
        api.post('/activities/time-tracking', {
          user_id: user.id,
          activity_id: currentActivity.id,
          week_number: parseInt(weekNumber),
          activity_title: currentActivity.title,
          time_spent_seconds: timeSpentSeconds
        }).catch(err => console.log('Time tracking failed:', err));
      }

      setInsight(response.data.insight.insight_text);
      setInsightId(response.data.insight.id);
      setTaskCompletions(response.data.insight.task_completions || {});
      setFeedbackGiven(null); // Reset feedback for new insight
      activityStartTime.current = null; // Reset timer
      
      // Show first activity card if this was their first activity
      if (isFirstActivity) {
        setShowFirstActivityCard(true);
        setIsFirstActivity(false); // Only show once
        // Persist to localStorage - track which activity to show it on
        localStorage.setItem('kodo_first_activity_card_week', String(weekNumber));
        localStorage.setItem('kodo_first_activity_card_order', String(currentActivityIndex + 1));
      }
      
      toast.success('AI feedback generated!');
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to generate feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInsightFeedback = async (isHelpful) => {
    if (!insightId || feedbackGiven !== null) return;
    
    const currentActivity = week?.activities[currentActivityIndex];
    
    try {
      await api.post('/insights/feedback', {
        user_id: user.id,
        insight_id: insightId,
        week_number: parseInt(weekNumber),
        activity_title: currentActivity?.title || '',
        is_helpful: isHelpful
      });
      
      trackAIInsightFeedback(parseInt(weekNumber), currentActivity?.title || '', isHelpful);
      setFeedbackGiven(isHelpful);
      toast.success('Thanks for your feedback!', { duration: 1500 });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleTaskToggle = async (taskKey) => {
    const newStatus = !taskCompletions[taskKey];
    
    try {
      await api.patch(`/insights/${insightId}/tasks`, {
        insight_id: insightId,
        task_key: taskKey,
        completed: newStatus
      });
      
      // Track homework checkbox usage
      trackHomeworkChecked(parseInt(weekNumber), taskKey, newStatus);
      
      setTaskCompletions(prev => ({
        ...prev,
        [taskKey]: newStatus
      }));
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleNextActivity = () => {
    if (currentActivityIndex < week.activities.length - 1) {
      setCurrentActivityIndex(currentActivityIndex + 1);
      setResponseText('');
      setInsight(null);
    }
  };

  const handlePreviousActivity = () => {
    if (currentActivityIndex > 0) {
      setCurrentActivityIndex(currentActivityIndex - 1);
      setResponseText('');
      setInsight(null);
    }
  };

  const handleDownload = () => {
    if (!insight) return;

    const currentActivity = week.activities[currentActivityIndex];
    
    // Sanitize text for cross-platform compatibility
    const sanitizeForDownload = (text) => {
      return text
        .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")  // Smart single quotes to straight
        .replace(/[\u201C\u201D]/g, '"')              // Smart double quotes to straight
        .replace(/\u2013/g, '-')                      // En dash
        .replace(/\u2014/g, '--')                     // Em dash
        .replace(/\u2026/g, '...')                    // Ellipsis
        .replace(/\u00A0/g, ' ')                      // Non-breaking space
        .replace(/\u2022/g, '*')                      // Bullet point
        .replace(/\u2192/g, '->')                     // Arrow
        .replace(/\u2190/g, '<-');                    // Left arrow
    };
    
    const sanitizedInsight = sanitizeForDownload(insight);
    const content = `Week ${weekNumber} - ${week.title}\n${currentActivity.title}\n\n${sanitizedInsight}`;
    
    // Use UTF-8 BOM for better compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `week${weekNumber}_${currentActivity.title.replace(/\s+/g, '_')}_feedback.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Feedback downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading week {weekNumber}...</p>
        </div>
      </div>
    );
  }

  if (!week) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="text-center">
          <p className="text-slate-600">Week not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const currentActivity = week.activities[currentActivityIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-button"
                variant="ghost"
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="text-sm font-medium text-primary">
                  Week {week.week_number} • Activity {currentActivityIndex + 1} of {week.activities.length}
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-900">
                  {week.title}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Activity Card */}
          <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 mb-8 animate-fade-in">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-primary font-bold text-lg">
                  {currentActivity.order}
                </div>
                <h2 className="font-heading text-3xl font-bold text-slate-900">
                  {currentActivity.title}
                </h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-4">
                {currentActivity.description}
              </p>
              
              {/* Time and Word Target */}
              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                {currentActivity.time_estimate && (
                  <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full">
                    <Clock className="w-4 h-4" />
                    {currentActivity.time_estimate}
                  </span>
                )}
                {currentActivity.word_target && (
                  <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full">
                    <Target className="w-4 h-4" />
                    Aim for {currentActivity.word_target}+ words
                  </span>
                )}
              </div>

              {/* Guidance Tips */}
              {currentActivity.guidance_tips && currentActivity.guidance_tips.length > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">Tips for a great response:</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {currentActivity.guidance_tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="activity-form">
              <div className="space-y-3">
                <Textarea
                  id="response"
                  data-testid="activity-response-textarea"
                  placeholder="Take your time to reflect and write your thoughts here..."
                  value={responseText}
                  onChange={handleResponseChange}
                  disabled={submitting || insight}
                  className="min-h-[250px] text-base border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-100 focus:border-primary"
                />
                {/* Word Counter */}
                {!insight && (
                  <div className="flex justify-between items-center mt-2 text-sm">
                    <span className={`${
                      responseText.trim().split(/\s+/).filter(w => w.length > 0).length >= (currentActivity.word_target || 200)
                        ? 'text-green-600'
                        : 'text-slate-500'
                    }`}>
                      {responseText.trim().split(/\s+/).filter(w => w.length > 0).length} words
                      {currentActivity.word_target && (
                        <span className="text-slate-400"> / {currentActivity.word_target} target</span>
                      )}
                    </span>
                    {responseText.trim().split(/\s+/).filter(w => w.length > 0).length >= (currentActivity.word_target || 200) && (
                      <span className="text-green-600 font-medium">✓ Target reached!</span>
                    )}
                  </div>
                )}
              </div>

              {!insight && (
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    data-testid="submit-response-button"
                    disabled={submitting}
                    className="h-12 px-8 rounded-full bg-primary text-white font-semibold shadow-lg hover:shadow-xl btn-hover"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {loadingMessage}
                      </>
                    ) : (
                      <>
                        Submit & Get Feedback
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </div>

          {/* First Activity Congratulations Card */}
          {showFirstActivityCard && insight && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-6 animate-slide-up" data-testid="first-activity-card">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <PartyPopper className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2">
                      Congratulations on completing your first activity!
                    </h3>
                    <p className="text-slate-700 mb-4">
                      Take a moment to read your personalised feedback below. You'll also find two weekly tasks:
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="text-slate-700"><strong>Solo</strong> — to do on your own</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-slate-700"><strong>Outreach</strong> — to do with others</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 italic">
                      Research shows combining solo reflection with external conversations increases the likelihood of lasting change by 42%.
                    </p>
                  </div>
                </div>
                <button 
                  onClick={handleDismissFirstActivityCard}
                  className="text-slate-400 hover:text-slate-600 p-1"
                  aria-label="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* AI Insight Display */}
          {insight && (
            <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 mb-8 animate-slide-up" data-testid="ai-feedback">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-2xl font-bold text-slate-900">
                  Your Personalised Feedback
                </h3>
                <Button
                  onClick={handleDownload}
                  data-testid="download-feedback-button"
                  variant="outline"
                  className="rounded-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              <div className="space-y-6">
                {formatInsight(insight, taskCompletions, handleTaskToggle)}
              </div>

              {/* Feedback buttons */}
              <div className="mt-8 pt-6 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <p className="text-slate-600 font-medium">Was this feedback helpful?</p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleInsightFeedback(true)}
                      disabled={feedbackGiven !== null}
                      variant={feedbackGiven === true ? "default" : "outline"}
                      className="rounded-full px-4 py-2"
                      data-testid="helpful-feedback-button"
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      {feedbackGiven === true ? "Thanks!" : "Yes"}
                    </Button>
                    <Button
                      onClick={() => handleInsightFeedback(false)}
                      disabled={feedbackGiven !== null}
                      variant={feedbackGiven === false ? "default" : "outline"}
                      className="rounded-full px-4 py-2"
                      data-testid="not-helpful-feedback-button"
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      {feedbackGiven === false ? "Thanks!" : "No"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4">
            <Button
              onClick={handlePreviousActivity}
              data-testid="previous-activity-button"
              disabled={currentActivityIndex === 0}
              variant="outline"
              className="rounded-full px-6"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous Exercise
            </Button>

            {currentActivityIndex === week.activities.length - 1 ? (
              <Button
                onClick={handleFinishWeek}
                data-testid="finish-week-button"
                disabled={!insight}
                className="rounded-full px-8 bg-primary text-white font-semibold btn-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Finish Week
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            ) : (
              <Button
                onClick={handleNextActivity}
                data-testid="next-activity-button"
                disabled={!insight}
                className="rounded-full px-6 bg-primary text-white font-semibold btn-hover"
              >
                Next Exercise
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            )}
          </div>

          {!insight && (
            <p className="text-center text-sm text-slate-500 mt-4">
              Complete this activity to {currentActivityIndex === week.activities.length - 1 ? 'finish this week' : 'move to the next exercise'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeekPage;
